
import Component from 'vue-class-component';
import { Watch, Ref, Mixins } from 'vue-property-decorator';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import { Container } from 'inversify';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import PolygonMap from '@/components/FieldAndPath/PolygonMap.vue';
import { getModule } from 'vuex-module-decorators';
import FieldStore from '../store/modules/fields';
import { FieldOperationsModel } from '@/flightpath-components/models/FieldOperationsModel';
import { VForm } from '../types/form';
import { Guid } from 'guid-typescript';
import { PubSub } from '@/PubSubClass';
import MqttMixIn from '@/mixins/mqttManagement';
import NotificationMixIn from '@/mixins/notifications';
import { Convert as GenericNotifyConvert } from '@/flightpath-components/models/mqttPayload/GenericNotificationMessage';
import { NotificationMessageType } from '@/flightpath-components/models/NotificationMessageType';
import { SortHelper } from '@/services/SortHelper';
import LastModifiedMixIn from '@/mixins/lastModified';
import GlobalStore from '@/store/modules/global';
import { PathHelper } from '@/utils/PathHelper';
import { GPSLocation } from '@/flightpath-components/models/GPSLocation';
import { SimplePath } from '@/flightpath-components/models/SimplePath';
import * as shapefile from "shapefile";
import shp from "shpjs";

// Define the component in class-style
/* eslint-disable @typescript-eslint/no-explicit-any */
@Component({ name: 'Fields', inject: ['container'], components: { PolygonMap } })
export default class Fields extends Mixins(MqttMixIn, NotificationMixIn, LastModifiedMixIn) {
  @Ref('form') readonly form!: VForm;
  public globalStore = getModule(GlobalStore, this.$store);
  public fieldStore = getModule(FieldStore, this.$store);
  type = [NotificationMessageType.NewField, NotificationMessageType.ModifiedField];
  panel = [0];
  editMode = false;
  fieldSelected = false;
  selectedFieldArea = 0;
  availableFields: FieldOperationsModel[] = [];
  dialog = false;
  emptyField: FieldOperationsModel = new FieldOperationsModel();
  selectedField = new FieldOperationsModel();
  newFieldName = '';
  valid = true;
  errorMessages = '';
  loading = false;
  deleteDialog = false;
  deleting = false;
  errorDialog = false;
  errorDialogMessage = '';

  // IoC container provided from App.ts is injected here
  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  private container!: Container;

  created(): void {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);
    this.addMqttVisibility(this.globalStore.generalNotificationTopic, () =>
      PubSub.subscribe(this.globalStore.generalNotificationTopic).subscribe({
        next: (data) => this.notificationRecieved(data),
        error: (error) => this.mqttError(error)
      })
    );
  }

  async mounted() {
    if (this.flightPathService != undefined) await this.flightPathService.updateToken();
    this.actions.forEach((act, key) => {
      this.subscriptions.set(key, act());
    });
  }
  destroyed() {
    this.removeMqttVisibility();
  }
  private async notificationRecieved(data: any): Promise<void> {
    const response = data.value as string;
    const status = GenericNotifyConvert.toGenericNotification(JSON.stringify(response));

    if (status.Type == NotificationMessageType.NewField) {
      const entityResponse = await this.flightPathService?.getFieldDefinition(status.EntityId, this.globalStore.accountId);
      const entity = JSON.parse(JSON.stringify(entityResponse?.data)) as FieldOperationsModel;

      const index = this.availableFields.findIndex((value) => value.FieldID == entity.FieldID);
      if (index < 0) {
        this.availableFields.push(entity);
        this.fieldStore.setFields(this.availableFields);
      } else {
        this.availableFields.splice(index, 1, entity);
        this.fieldStore.setFields(this.availableFields);
      }
    }
  }

  @Watch('fieldStore.editMode')
  onEditModeChange(): void {
    this.editMode = this.fieldStore.editFieldMode;
  }

  @Watch('fieldStore.fieldArea')
  onFieldAreaChange(): void {
    this.selectedFieldArea = this.fieldStore.selectedFieldArea;
  }

  addField() {
    if (this.fieldStore.editFieldMode) return;

    if (this.form.validate()) {
      this.selectedFieldArea = 0;
      this.fieldSelected = false;
      this.selectedField = new FieldOperationsModel();
      this.selectedField.FieldName = this.newFieldName;
      this.selectedField.FieldID = Guid.create().toString();
      this.fieldStore.toggleAddMode();
      this.fieldStore.toggleEditMode();
      this.dialog = false;
    }
  }

  resetForm() {
    this.newFieldName = '';
    if (this.form) this.form.resetValidation();
    this.dialog = true;
  }

  clearAddForm() {
    this.dialog = false;
  }

  async getAvailableFields() {
    if (!this.flightPathService) return;

    const response = await this.flightPathService.getAvailableFields(this.globalStore.accountId);
    this.availableFields = FieldOperationsModel.toModelCollection(JSON.stringify(response.data));
    this.availableFields = this.availableFields.sort((a, b) => SortHelper.compareStrings(a.FieldName as string, b.FieldName as string));
    this.fieldStore.setFields(this.availableFields);
  }

  async selectField(field: FieldOperationsModel) {
    if (field.FieldID == this.selectedField.FieldID) return;
    if (this.fieldStore.editFieldMode) return;
    this.updateSelected(field.FieldID);
    this.fieldSelected = true;
    this.selectedField = field;
    this.fieldStore.setSelectedField(this.selectedField);
  }

  editField() {
    if (this.fieldSelected) this.fieldStore.toggleEditMode();

    if (!this.fieldStore.editFieldMode && !this.fieldStore.addFieldMode) {
      this.fieldStore.triggerRevertBoundary();
    }

    if (this.fieldStore.addFieldMode) {
      this.clearMapAndSelectedField();
      this.fieldStore.toggleAddMode();
      this.fieldStore.toggleEditMode();
    }
  }

  clearMapAndSelectedField() {
    this.fieldStore.triggerClearFieldMap();
    this.selectedField = new FieldOperationsModel();
    this.fieldSelected = false;
    this.selectedFieldArea = 0;
  }

  onFileChanged(e: any) {
    this.changePolygonPathFromFile(e.target.files[0] as File);
  }

  onUploadPath() {
    if (this.$refs.uploader == undefined) return;
    if (this.$refs.uploader instanceof HTMLElement) {
      this.$refs.uploader.click();
    }
  }

  async changePolygonPathFromFile(file: File) {
    this.loading = true;
    try {
      var path = [] as GPSLocation[];

      if(file.name.endsWith('.zip')) {
        const buffer = await file.arrayBuffer();
        const geojson = await shp(buffer);
        geojson.features.forEach(feature => {
          feature.geometry.coordinates.forEach((coord: number[][]) => {
            coord.forEach((coordPoint: number[]) => {
              path.push({
                Latitude: coordPoint[1],
                Longitude: coordPoint[0]
              } as GPSLocation);
            });
          });
        });
      } else if(file.name.endsWith('.shp')) {
        const arrayBuffer = await file.arrayBuffer();
        const source = await shapefile.openShp(arrayBuffer);
        var cont = true;
        while (cont) {
          const result = await source.read();
          if (result.done) break;

          path.push({
            Latitude: result.value.coordinates[1],
            Longitude: result.value.coordinates[0]
          } as GPSLocation)
        }
      }else if(file.name.endsWith('.json')) {
        const fileContents = await file.text();
        if (fileContents == undefined) return;
          const result = JSON.parse(fileContents) as SimplePath;
        if (result.Path.length == 0) {
          console.error('path has no points');
        }
        path = result.Path.map((value) => {
          return { Latitude: value.Lat, Longitude: value.Long, Elevation: value.Alt } as GPSLocation;
        });
      }

      const distinctPath = PathHelper.removeDuplicateSuccessivePoints(path);

      // set the new Field Boundary
      this.fieldStore.setPathToPolygon(distinctPath.map((value) => 
        {
          return new google.maps.LatLng(value.Latitude, value.Longitude);
        })
      );
    } catch (error) {
      console.error(error);
      this.errorDialog = true;
      this.errorDialogMessage = (error as Error).message;
    } finally {
      this.loading = false;
    }
  }

  async cleanField(){
      this.fieldStore.setPathToPolygon([]);
  }

  async saveField() {
    if (!this.flightPathService) return;

    this.fieldStore.toggleEditMode();
    this.selectedField.BoundaryPoints = this.fieldStore.polygon.map((val) => ({ Item1: val.lat(), Item2: val.lng() }));

    const finalField = FieldOperationsModel.toModel(this.selectedField.fromModelToJson());

    finalField.CustomerID = this.globalStore.accountId;

    if (this.fieldStore.addFieldMode) {
      this.fieldStore.toggleAddMode();
      this.clearMapAndSelectedField();
    }

    await this.flightPathService.addFieldDefinition(finalField);

    await this.getAvailableFields();
  }

  async deleteField() {
    if (!this.flightPathService) return;

    this.deleting = true;
    try {
      await this.flightPathService.deleteFieldDefinition(this.selectedField.FieldID);
      this.$emit('deleted', this.selectedField);
      this.clearMapAndSelectedField();
      await this.getAvailableFields();
      this.deleteDialog = false;
    } finally {
      this.deleting = false;
    }
  }
}
